import { DateTime } from "luxon"
import { Desglose, DesgloseTarifa, FechaValida, GrupoFormato, GrupoTarifa } from "../useMotorTarifas.type"
import { ModalidadGrupoTarifa } from "src/gql/schema"
import { getPrioridad } from "./prioridad"
import { add, times } from "src/shared/helpers/calculator"
import { months } from "src/utils/date"

function formatoFecha(fecha: string) {
    const date = DateTime.fromISO(fecha.substring(0, 10))
    return `${months?.[date.month - 1]?.slice(0, 3)}, ${date.day}`
}

export function getGruposFormato(grupos: GrupoTarifa[], zonehotel: string): GrupoFormato[] {
    // Objeto acumulador donde se agrupan los resultados por tarifa_id
    const secciones: Record<string, GrupoFormato> = {}

    // Recorre cada grupo de tarifas previamente calculado
    for (const g of grupos) {

        // Si aún no existe una sección para esta tarifa, se inicializa
        if (!secciones[g.tarifa_id]) {
            secciones[g.tarifa_id] = {
                tarifa: g.tarifa,
                subtotal_hospedaje: 0,
                dias: 0,
                hospedaje: [],
                tarifa_id: g.tarifa_id
            }
        }

        // Formatea las fechas de inicio y fin para presentación
        const fechaInicio = formatoFecha(g.fecha_inicio)
        const fechaFin = formatoFecha(g.fecha_fin)

        // Construye el rango de fechas:
        // - Si es un solo día, muestra solo la fecha
        // - Si son varios días, muestra el rango completo
        const rango =
            g.dias === 1
                ? `${fechaInicio}`
                : `${fechaInicio} - ${fechaFin}`

        // Asegura que el subtotal sea numérico
        const subtotal_hospedaje = Number(g.subtotal_hospedaje || 0)

        // Agrega el bloque de hospedaje a la tarifa correspondiente
        secciones[g.tarifa_id].hospedaje.push({
            rango,
            costo_hospedaje: Number(g.costo_hospedaje || 0),
            subtotal_hospedaje,
            fecha_inicio: g.fecha_inicio,
            fecha_fin: g.fecha_fin,
            dias: g.dias
        })

        // Acumula el subtotal total por tarifa
        secciones[g.tarifa_id].subtotal_hospedaje += subtotal_hospedaje

        // Acumula el total de días por tarifa
        secciones[g.tarifa_id].dias += g.dias
    }

    // Convierte el objeto acumulador a arreglo
    const values = Object.entries(secciones).map(([tarifa_id, value]) => ({
        tarifa_id,
        hospedaje: value.hospedaje,
        tarifa: value.tarifa,
        dias: value.dias,
        subtotal_hospedaje: value.subtotal_hospedaje,

        // Calcula la prioridad de la tarifa para ordenamiento posterior
        prioridad: getPrioridad(value?.tarifa),
    }))

    // Filtra valores válidos y ordena por prioridad (ascendente)
    return values
        .filter((v) => v)
        .sort((a, b) => a.prioridad - b.prioridad)
}

function expandirFechasHospedaje(hospedaje: GrupoFormato["hospedaje"]): FechaValida[] {
    const fechas: FechaValida[] = []
    for (const h of hospedaje) {
        const fechaActual = new Date(h.fecha_inicio)
        const fechaFin = new Date(h.fecha_fin)
        while (fechaActual <= fechaFin) {
            fechas.push([new Date(fechaActual), h.costo_hospedaje])
            fechaActual.setDate(fechaActual.getDate() + 1)
        }
    }
    return fechas
}

type GetDesgloseParams = {
    grupos: GrupoTarifa[]
    horas_extra: number,
    personas_extra: number,
    hospedajes_extra: number,
    fecha_inicio: string
    fecha_fin: string
    zonehotel: string
}

export function getDesglose({
    grupos = [],
    horas_extra = 0,
    hospedajes_extra = 0,
    personas_extra = 0,
    fecha_inicio = "",
    fecha_fin = "",
    zonehotel
}: GetDesgloseParams): Desglose {

    // Arreglo que contendrá el desglose final por tarifa
    const desglose: DesgloseTarifa[]  = []

    // Acumulador del total general
    let total = 0

    // Variable para almacenar el costo de early check-in (si aplica)
    let costo_early_checkin = 0

    // Transforma los grupos en un formato más adecuado para presentación
    const grupos_formato = grupos.length > 0 ? getGruposFormato(grupos, zonehotel) : []

    // Objeto con la tarifa del primer dia
    let tarifa_primer_dia: any = null

    // Objeto con la tarifa del ulltimo dia
    let tarifa_ultimo_dia: any = null

    // Itera sobre cada grupo formateado (agrupado por tarifa)
    grupos_formato.forEach((grupo) => {

        // Obtiene la modalidad de la tarifa (ej. Base, etc.)
        const modalidad = grupo?.tarifa?.grupo_tarifa?.modalidad || ""

        // Calcula subtotales de cargos adicionales multiplicando por las cantidades
        const subtotal_horas_extra = times(
            Number(grupo?.tarifa?.costo_hora_extra || 0),
            horas_extra
        )

        const subtotal_personas_extra = times(
            times(Number(grupo?.tarifa?.costo_persona_extra || 0), personas_extra),
            grupo.dias
        )

        const subtotal_hospedajes_extra = times(
            Number(grupo?.tarifa?.costo_hospedaje_extra || 0),
            hospedajes_extra
        )

        // Identifica si el grupo contiene el último día del rango
        const ultimo_dia = grupo?.hospedaje?.find(
            (i) => i.fecha_fin === fecha_fin
        )

        // Identifica si el grupo contiene el primer día del rango
        const primer_dia = grupo?.hospedaje?.find(
            (i) => i.fecha_inicio === fecha_inicio
        )

        // Construye el objeto final del desglose por tarifa
        const item = {
            ...grupo,

            // Días individuales con su monto para esta tarifa
            fechas_validas: expandirFechasHospedaje(grupo.hospedaje),

            // Subtotal por personas extra (se aplica siempre)
            subtotal_personas_extra,
            personas_extra,

            // Ajusta el nombre de la tarifa si es modalidad Base
            tarifa: {
                ...grupo.tarifa,
                nombre: modalidad === ModalidadGrupoTarifa.Base
                    ? "Base"
                    : grupo?.tarifa?.nombre
            },

            // Aplica cargos de extras SOLO si el grupo contiene el último día
            ...(ultimo_dia
                ? {
                    subtotal_horas_extra,
                    subtotal_hospedajes_extra,
                    horas_extra,
                    hospedajes_extra
                }
                : {
                    subtotal_horas_extra: 0,
                    subtotal_hospedajes_extra: 0,
                    horas_extra: 0,
                    hospedajes_extra: 0
                })
        }

        // Agrega el item al desglose
        desglose.push(item)

        // Suma al total general:
        // - hospedaje base
        // - horas extra
        // - hospedajes extra
        // - personas extra
        const subtotal_item =
            item.subtotal_hospedaje +
            item.subtotal_horas_extra +
            item.subtotal_hospedajes_extra +
            item.subtotal_personas_extra

        total = add(total, subtotal_item)

        // Si el grupo contiene el primer día, captura el costo de early check-in
        if (primer_dia && !tarifa_primer_dia) {
            costo_early_checkin = Number(
                grupo?.tarifa?.costo_early_checkin || 0
            )
            tarifa_primer_dia = grupo?.tarifa
        }

        if(ultimo_dia && !tarifa_ultimo_dia) {
            tarifa_ultimo_dia = grupo?.tarifa
        }
    })

    // Retorna el desglose completo junto con el total y el early check-in
    return {
        desglose,
        total,
        costo_early_checkin,
        tarifa_primer_dia: (tarifa_primer_dia || grupos?.[0]?.tarifa),
        tarifa_ultimo_dia: (tarifa_ultimo_dia || grupos?.[grupos.length - 1]?.tarifa),
    }
}